import { afterNextRender, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, ViewEncapsulation } from '@angular/core';

import type { ClassValue } from 'clsx';
import { NgxSonnerToaster } from 'ngx-sonner';

import { toastVariants, type ZardToastVariants } from './toast.variants';

import { mergeClasses } from '@/shared/utils/merge-classes';

@Component({
  selector: 'z-toast, z-toaster',
  imports: [NgxSonnerToaster],
  standalone: true,
  template: `
    <ngx-sonner-toaster
      [theme]="theme()"
      [class]="classes()"
      [position]="position()"
      [richColors]="richColors()"
      [expand]="expand()"
      [duration]="duration()"
      [visibleToasts]="visibleToasts()"
      [closeButton]="closeButton()"
      [toastOptions]="resolvedToastOptions()"
      [dir]="dir()"
    />
  `,
  // A manual popover sits in the browser's top layer, like the CDK dialogs (which beat any z-index there).
  host: { popover: 'manual' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  exportAs: 'zToast',
})
export class ZardToastComponent {
  constructor() {
    const host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    afterNextRender(() => bringToasterToFront(host));
  }

  readonly class = input<ClassValue>('');
  readonly variant = input<ZardToastVariants['variant']>('default');
  readonly theme = input<'light' | 'dark' | 'system'>('system');
  readonly position = input<'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'>(
    'bottom-right',
  );

  readonly richColors = input<boolean>(false);
  readonly expand = input<boolean>(false);
  readonly duration = input<number>(4000);
  readonly visibleToasts = input<number>(3);
  readonly closeButton = input<boolean>(false);
  readonly toastOptions = input<Record<string, unknown>>({});
  readonly dir = input<'ltr' | 'rtl' | 'auto'>('auto');

  protected readonly classes = computed(() =>
    mergeClasses('toaster group', toastVariants({ variant: this.variant() }), this.class()),
  );

  protected readonly resolvedToastOptions = computed(() => ({
    style: {
      // Prevent background dimming when toasts appear
      backdropFilter: 'none',
    },
    ...this.toastOptions(),
  }));
}

/**
 * Puts the toaster on top of the top layer: re-showing a popover moves it above the popovers (dialogs) opened after
 * it. Without popover support the toaster keeps its z-index.
 */
export function bringToasterToFront(host: HTMLElement | null = document.querySelector<HTMLElement>('z-toast[popover], z-toaster[popover]')) {
  if (!host || typeof host.showPopover !== 'function') return;
  if (host.matches(':popover-open')) host.hidePopover();
  host.showPopover();
}
